import React from 'react';

import type { PlateElementProps } from 'platejs/react';
import { ParagraphPlugin as BaseParagraphPlugin, PlateElement, useFocused } from 'platejs/react';

import {
  EMPTY_PARAGRAPH_ACTIONS_WIDTH,
  EmptyParagraphActions,
  useShowEmptyParagraphActions,
} from '../empty-paragraph-actions';

/**
 * Paragraph element component.
 * Renders as <div> for list items (to avoid <p> inside <li>) or <p> otherwise.
 */
export const ParagraphElement = React.memo(function ParagraphElement(props: PlateElementProps) {
  /**
   * Check if this paragraph is part of a list (has listStyleType)
   * If so, render as a div instead of <p> to avoid <p> inside <li>
   */
  const isListItem = props.element.listStyleType !== undefined;
  // Show insertion actions on the current empty paragraph.
  const showActions = useShowEmptyParagraphActions() && !isListItem;
  const focused = useFocused();

  return (
    <PlateElement
      {...props}
      as={isListItem ? 'div' : 'p'}
      style={{
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        /**
         * Anchor the inline actions to this line and keep the placeholder clear of them. The
         * line keeps its own height: the actions overhang it, so showing them moves nothing.
         */
        ...(showActions && {
          paddingRight: focused ? EMPTY_PARAGRAPH_ACTIONS_WIDTH : 0,
          position: 'relative',
        }),
      }}
    >
      {props.children}
      {showActions && <EmptyParagraphActions />}
    </PlateElement>
  );
});

/**
 * Configured ParagraphPlugin with custom element component.
 */
export const ParagraphPlugin = BaseParagraphPlugin.withComponent(ParagraphElement);
